import React, { useContext, useRef, useState } from 'react';
import {
  FormControlLabel,
  Checkbox,
  Tooltip,
  Typography,
  Box,
  FormHelperText,
  IconButton,
  CircularProgress,
  FormLabel,
} from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import {
  LocalizationProvider,
  DateTimePicker,
  DatePicker,
} from '@mui/x-date-pickers';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import OutlinedInput from '@mui/material/OutlinedInput';
import { IncidentContext } from './IncidentContext';
import AutocompleteFactory from './AutocompleteFactory';
import moment from 'moment';

const OPENAI_KEY = process.env.REACT_APP_OPENAI_API_KEY;

const OpenPickerIcon = () => <ArrowDropDownIcon fontSize="large" />;

const disabledStyles = {
  backgroundColor: '#f5f5f5',
};

const dateTheme = {
  width: '100%',
  '.MuiInputLabel-root': {
    lineHeight: 0.6,
    overflow: 'visible',
  },
  '& .MuiInputBase-root': {
    padding: 0,
    height: '40px',
    '& .MuiButtonBase-root': {
      marginRight: '-2px',
      padding: '2px',
      textAlign: 'center',
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',
      '& svg': {
        fontSize: '1.5rem',
      },
    },
    '& .MuiInputBase-input': {
      padding: '16px 14px',
    },
    '& .MuiInputAdornment-root': {
      position: 'absolute',
      right: '9px',
    },
  },
};

export const InputFactory = ({
  item,
  field,
  error,
  readonly,
  multiple = false,
  dependentFields,
}) => {
  const incident = useContext(IncidentContext);
  const [recording, setRecording] = useState(false);
  const [loading, setLoading] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mediaRecorder = new MediaRecorder(stream);
    mediaRecorderRef.current = mediaRecorder;
    audioChunksRef.current = [];

    mediaRecorder.ondataavailable = (e) => {
      audioChunksRef.current.push(e.data);
    };

    mediaRecorder.onstop = async () => {
      const audioBlob = new Blob(audioChunksRef.current, {
        type: 'audio/webm',
      });
      const formData = new FormData();
      formData.append('file', audioBlob, 'audio.webm');
      formData.append('model', 'gpt-4o-transcribe');

      setLoading(true);
      try {
        const response = await fetch(
          'https://api.openai.com/v1/audio/transcriptions',
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${OPENAI_KEY}`,
            },
            body: formData,
          }
        );

        const data = await response.json();
        if (data.text) {
          field.onChange(data.text);
        } else {
          console.error('Transcription failed', data);
        }
      } catch (err) {
        console.error('Error sending audio:', err);
      } finally {
        setLoading(false);
      }
    };

    mediaRecorder.start();
    setRecording(true);
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  const renderLabel = () => (
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Box
        sx={{
          display: 'flex',
          gap: '4px',
        }}
      >
        <FormLabel
          htmlFor={field.name}
          variant="subtitle1"
          fontWeight={500}
          style={{
            fontSize: '1rem',
            fontWeight: 500,
            margin: 0,
          }}
        >
          {item.required && (
            <Typography color="error" component="span">
              *
            </Typography>
          )}
          {item.label}
        </FormLabel>

        {item.description && (
          <Tooltip title={item.description} placement="top" arrow>
            <HelpOutlineIcon
              sx={{
                width: '0.55em',
                marginTop: '-3px',
              }}
            />
          </Tooltip>
        )}
      </Box>
      {!readonly &&
        (item.key === 'impediment_narrative' ||
          item.key === 'outcome_narrative') && (
          <Box>
            <IconButton
              onClick={recording ? stopRecording : startRecording}
              size="small"
            >
              {loading ? (
                <CircularProgress size={20} />
              ) : recording ? (
                <StopIcon color="error" />
              ) : (
                <MicIcon color="primary" />
              )}
            </IconButton>
          </Box>
        )}
    </Box>
  );

  const renderError = () => (
    <FormHelperText
      id={field.name}
      sx={{ color: 'error.main', mt: 0.5, ml: 0 }}
    >
      {error?.message}
    </FormHelperText>
  );

  const renderInput = () => {
    switch (item.type) {
      case 'text':
      case 'number':
        return (
          <>
            {renderLabel()}
            <OutlinedInput
              id={field.name}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              inputRef={field.ref}
              name={field.name}
              type={item.type}
              placeholder={
                item.type === 'number' &&
                "N/A (Leave blank if you don't have any details)"
              }
              fullWidth
              variant="outlined"
              size="small"
              disabled={readonly}
              sx={readonly ? disabledStyles : {}}
            />
            {renderError()}
          </>
        );

      case 'textarea':
        return (
          <>
            {renderLabel()}
            <OutlinedInput
              id={field.name}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              inputRef={field.ref}
              name={field.name}
              fullWidth
              variant="outlined"
              multiline
              rows={4}
              size="small"
              disabled={readonly}
              sx={readonly ? disabledStyles : {}}
            />
            {renderError()}
          </>
        );

      case 'select':
        return (
          <>
            {renderLabel()}
            <AutocompleteFactory
              item={item}
              field={field}
              multiple={multiple}
              readonly={readonly}
              disabledStyles={disabledStyles}
              incident={incident}
              dependentFields={dependentFields}
            />
            {renderError()}
          </>
        );

      case 'checkbox':
        return (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  {...field}
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  size="small"
                  disabled={readonly}
                  id={field.name}
                />
              }
              label={
                <Box
                  sx={{
                    display: 'flex',
                    gap: '4px',
                  }}
                >
                  {item.required && (
                    <Typography color="error" component="span">
                      *
                    </Typography>
                  )}
                  <Typography
                    variant="subtitle1"
                    component="span"
                    fontWeight={500}
                  >
                    {item.label}
                  </Typography>
                  {item.description && (
                    <Tooltip title={item.description} placement="top" arrow>
                      <HelpOutlineIcon
                        sx={{
                          width: '0.55em',
                          marginTop: '-3px',
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
              }
              sx={readonly ? disabledStyles : {}}
            />
            {renderError()}
          </>
        );

      case 'datetime':
        return (
          <>
            {renderLabel()}
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DateTimePicker
                id={field.name}
                value={field.value || null}
                onChange={(date) => field.onChange(date)}
                slots={{ openPickerIcon: OpenPickerIcon }}
                format="MM/DD/YYYY HH:mm:ss"
                ampm={false}
                disabled={readonly}
                slotProps={{
                  actionBar: { actions: ['clear', 'today'] },
                  textField: {
                    inputRef: field.ref,
                    variant: 'outlined',
                    size: 'small',
                    fullWidth: true,
                    color: 'secondary',
                    sx: readonly ? disabledStyles : {},
                  },
                }}
                sx={dateTheme}
              />
            </LocalizationProvider>
            {renderError()}
          </>
        );

      case 'date':
        return (
          <>
            {renderLabel()}
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                id={field.name}
                value={field.value || null}
                onChange={(date) => field.onChange(date)}
                slots={{ openPickerIcon: OpenPickerIcon }}
                format="MM/DD/YYYY"
                ampm={false}
                disabled={readonly}
                slotProps={{
                  actionBar: { actions: ['clear', 'today'] },
                  textField: {
                    inputRef: field.ref,
                    variant: 'outlined',
                    size: 'small',
                    fullWidth: true,
                    color: 'secondary',
                    sx: readonly ? disabledStyles : {},
                  },
                }}
                sx={dateTheme}
              />
            </LocalizationProvider>
            {renderError()}
          </>
        );

      case 'month':
        return (
          <>
            {renderLabel()}
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                id={field.name}
                value={field.value || null}
                onChange={(date) => {
                  // Use UTC to avoid timezone shift when sending date to API.
                  // Moment object was being converted to JSON by axios, causing -3h offset.
                  // With month picker this resulted in sending the previous month.
                  // Storing value in UTC prevents any unwanted conversion.

                  field.onChange(moment.utc(date.format('MM/YYYY'), 'MM/YYYY'));
                }}
                slots={{ openPickerIcon: OpenPickerIcon }}
                format="MM/YYYY"
                ampm={false}
                disabled={readonly}
                views={['month', 'year']}
                slotProps={{
                  actionBar: { actions: ['clear', 'today'] },
                  textField: {
                    inputRef: field.ref,
                    variant: 'outlined',
                    size: 'small',
                    fullWidth: true,
                    color: 'secondary',
                    sx: readonly ? disabledStyles : {},
                  },
                }}
                sx={dateTheme}
              />
            </LocalizationProvider>
            {renderError()}
          </>
        );

      default:
        return <Typography color="error">Unknown type: {item.type}</Typography>;
    }
  };

  return <Box>{renderInput()}</Box>;
};
